import { includesPeriod } from './periods';

import type { StatementFile } from 'modules/common/components/VerifiedStatementUpload/components/StatementSlotList';
import { Document } from 'modules/common/interfaces/documents';
import { HeldStatement, StatementPeriod } from 'services/bookkeeping/queries/useStatementCoverage';

/** The member's statement documents, by uuid — the subset of each this flow reads. */
export type StatementDocumentsByUuid = ReadonlyMap<string, Pick<Document, 'file_name'>>;

/** Sources that deliver a statement themselves. Compared lower-case, as the server spells them. */
const BANK_SOURCES = new Set(['quiltt', 'relay']);

/** Whether a held month came from the bank (a row with no document counts) — nothing the member can move. */
export function isBankDelivered(held: HeldStatement): boolean {
    return held.member_document_uuid == null || BANK_SOURCES.has(held.statement_source.toLowerCase());
}

interface HeldHereFilesArgs {
    /** The files the caller lists for this card. */
    files: StatementFile[];
    /** This card's held months, from coverage. */
    heldPeriods: HeldStatement[];
    /** The months this window asks for. */
    askedPeriods: StatementPeriod[];
    documentsByUuid: StatementDocumentsByUuid;
}

/**
 * Nameable uploads the caller doesn't list that hold an asked month here — e.g. a combined PDF
 * dropped on another card — so the month never reads "Missing" while the card says it's covered.
 * Asked months only: anything else would land in the couldn't-match list.
 */
export default function heldHereFiles({
    files,
    heldPeriods,
    askedPeriods,
    documentsByUuid,
}: HeldHereFilesArgs): StatementFile[] {
    const listed = new Set(files.map((file) => file.documentUuid));
    const holders = new Set<string>();
    heldPeriods.forEach((held) => {
        const documentUuid = held.member_document_uuid;
        if (
            documentUuid &&
            !isBankDelivered(held) &&
            !listed.has(documentUuid) &&
            documentsByUuid.has(documentUuid) &&
            includesPeriod(askedPeriods, held)
        ) {
            holders.add(documentUuid);
        }
    });
    return [...holders].map((documentUuid) => ({
        documentUuid,
        // Checked in the filter above; `has` does not narrow a later `get` for tsc.
        fileName: documentsByUuid.get(documentUuid)?.file_name ?? '',
    }));
}
